import { useState, useMemo } from "react";
import { format, parseISO, isValid } from "date-fns";
import EntityInformationModal from "../../components/modals/EntityInformationModal";
import EntityCalendar from "../../components/EntityCalendar";

export default function MaintenanceSchedulesCalendar({
    devices = [],
    onRefresh = async () => {},
    onEditSchedule,
    filterText = "",
}) {
    const [modalItem, setModalItem] = useState(null);

    const getColorForDates = (start, end) => {
        const now = new Date();
        if (now < start) return "#3b82f6";
        if (end && now > end) return "#6b7280";
        return "#10b981";
    };

    const coerceISO = (v) => {
        if (!v) return null;
        if (typeof v === "string") {
            const d = parseISO(v);
            return isValid(d) ? d : null;
        }
        if (v instanceof Date && isValid(v)) return v;
        try {
            const d = parseISO(String(v));
            return isValid(d) ? d : null;
        } catch {
            return null;
        }
    };

    const parseScheduleWindow = (schedule) => {
        let attrs = schedule?.schedule_attributes;
        if (!attrs) return { start: null, end: null };

        if (typeof attrs === "string") {
            try {
                attrs = JSON.parse(attrs);
            } catch {
                attrs = { when: attrs };
            }
        }

        const start = coerceISO(attrs?.from);
        const end = coerceISO(attrs?.to);

        return { start, end };
    };

    const calendarEvents = useMemo(() => {
        const events = [];
        const q = filterText?.trim().toLowerCase();
        const filteredDevices = !q
            ? devices
            : devices.filter((dev) =>
                  String(dev.name || dev.id || "")
                      .toLowerCase()
                      .includes(q),
              );

        filteredDevices.forEach((dev) => {
            (dev.maintenance_schedules || []).forEach((ms, idx) => {
                const { start, end } = parseScheduleWindow(ms);
                if (!start) return;

                const color =
                    ms.is_active === false
                        ? "#adb5bd"
                        : getColorForDates(start, end || start);
                // A window that spans days stays one timed event: FullCalendar splits
                // it per day column in the week/day grids and draws one bar across the
                // month grid.
                events.push({
                    id: `${dev.id}-${idx}`,
                    title: `${dev.name || dev.id || "Device"}`,
                    start,
                    end: end || undefined,
                    color,
                    extendedProps: {
                        device: dev,
                        schedule: ms,
                        start,
                        end,
                    },
                });
            });
        });
        return events;
    }, [devices, filterText]);

    const handleEventClick = (eventInfo) => {
        const { device, schedule, start, end } = eventInfo.event.extendedProps;
        const attrs =
            typeof schedule?.schedule_attributes === "string"
                ? (() => {
                      try {
                          return JSON.parse(schedule.schedule_attributes);
                      } catch {
                          return { when: schedule.schedule_attributes };
                      }
                  })()
                : schedule?.schedule_attributes || {};
        setModalItem({
            device,
            schedule,
            start,
            end,
            attrs,
        });
    };

    return (
        <>
            <EntityCalendar
                events={calendarEvents}
                eventClick={handleEventClick}
            />

            <EntityInformationModal
                show={!!modalItem}
                onHide={() => setModalItem(null)}
                title={modalItem?.schedule?.name || "Maintenance Schedule"}
                onEdit={() => {
                    if (onEditSchedule) {
                        onEditSchedule({
                            deviceId: modalItem.device?.id,
                            schedule: modalItem.schedule,
                        });
                    }
                    setModalItem(null);
                }}
                isEditing={false}
                editTitle="Edit Maintenance"
            >
                <div className="tw-space-y-3">
                    <div className="tw-space-y-1">
                        <div>
                            <span className="tw-font-medium">Device:</span>{" "}
                            {modalItem?.device?.name || modalItem?.device?.id}
                        </div>
                        {modalItem?.start && (
                            <div>
                                <span className="tw-font-medium">
                                    When/From:
                                </span>{" "}
                                {format(modalItem.start, "PPpp")}
                            </div>
                        )}
                        {modalItem?.end && (
                            <div>
                                <span className="tw-font-medium">To:</span>{" "}
                                {format(modalItem.end, "PPpp")}
                            </div>
                        )}
                    </div>

                    {modalItem?.schedule?.description && (
                        <div className="tw-text-sm tw-text-gray-700">
                            <div className="tw-font-medium tw-mb-1">
                                Description:
                            </div>
                            <div className="tw-text-gray-600">
                                {modalItem.schedule.description}
                            </div>
                        </div>
                    )}

                    {(modalItem?.schedule?.updated_by ||
                        modalItem?.schedule?.updated_at) && (
                        <>
                            <hr className="tw-border tw-border-solid tw-border-gray-300" />
                            <div className="tw-text-xs tw-text-gray-500 tw-italic">
                                {modalItem?.schedule?.updated_by &&
                                modalItem?.schedule?.updated_at ? (
                                    <>
                                        Updated by{" "}
                                        {modalItem.schedule.updated_by.name} on{" "}
                                        {format(
                                            new Date(
                                                modalItem.schedule.updated_at,
                                            ),
                                            "PPp",
                                        )}
                                    </>
                                ) : modalItem?.schedule?.created_by &&
                                  modalItem?.schedule?.created_at ? (
                                    <>
                                        Created by{" "}
                                        {modalItem.schedule.created_by.name} on{" "}
                                        {format(
                                            new Date(
                                                modalItem.schedule.created_at,
                                            ),
                                            "PPp",
                                        )}
                                    </>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
            </EntityInformationModal>
        </>
    );
}
